import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "./AdminShared.css";

const API_BASE = `${API_BASE_URL}/api/products`;

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    category: "",
    stock: "",
    features: "",
    specifications: "",
    image: null,
  });

  const [existingImage, setExistingImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Convert Array → Newline Text
  const arrToText = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

  /* =========================
        FETCH PRODUCT
  ========================= */
  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        const adminInfo = JSON.parse(localStorage.getItem("adminInfo"));
        const token = adminInfo?.token;
        const res = await fetch(`${API_BASE}/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error("Product not found");
        const data = await res.json();
        setFormData({
          name: data.name || "",
          price: data.price || "",
          description: data.description || "",
          category: data.category || "",
          stock: data.stock || "",
          features: arrToText(data.features),
          specifications: arrToText(data.specifications),
          image: null,
        });
        setExistingImage(data.image || null);
      } catch (err) { setError(err.message || "Failed to load product"); }
      finally { setLoading(false); }
    };
    loadProduct();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files?.length > 0) {
      setFormData((prev) => ({ ...prev, image: files[0] }));
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* =========================
        SUBMIT FORM
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const feat = formData.features.split("\n").map(i => i.trim()).filter(Boolean);
    const spec = formData.specifications.split("\n").map(i => i.trim()).filter(Boolean);
    const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;

    const data = new FormData();
    data.append("name", formData.name);
    data.append("price", formData.price);
    data.append("description", formData.description);
    data.append("category", formData.category);
    data.append("stock", formData.stock);
    data.append("features", JSON.stringify(feat));
    data.append("specifications", JSON.stringify(spec));
    if (formData.image) data.append("image", formData.image);

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "PUT",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: data,
      });
      if (!res.ok) throw new Error("Update failed");
      setSuccess("✅ Hardware asset synchronized.");
      setTimeout(() => navigate("/admin/panel"), 1500);
    } catch (err) { setError("Server error."); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="admin-list-container"><p style={{padding: '100px', textAlign: 'center'}}>⏳ Scanning Hardware Profile...</p></div>;

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <button className="admin-back-btn" onClick={() => navigate("/admin/panel")}>←</button>
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Hardware Synchronization</h1>
        </div>
        <div className="module-badge">Edit Fragment</div>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
           <div className="admin-user-tag">
              <div className="dot" style={{ background: '#6366f1' }}></div>
              Operator: Logistical Systems Node
           </div>
        </div>

        <div className="admin-table-wrapper" style={{ padding: '40px', background: '#fff' }}>
            <form className="admin-form-elite" onSubmit={handleSubmit}>
                <label>Asset Identity <input type="text" name="name" value={formData.name} onChange={handleChange} required /></label>
                <label>Unit Valuation (INR) <input type="number" name="price" value={formData.price} onChange={handleChange} required /></label>
                <label className="full-width-field">Technical Abstract <textarea name="description" value={formData.description} onChange={handleChange} required /></label>
                <label>Operational Category <input type="text" name="category" value={formData.category} onChange={handleChange} required /></label>
                <label>Stock Buffer <input type="number" name="stock" value={formData.stock} onChange={handleChange} required /></label>
                <label>Feature Log (one per line) <textarea name="features" value={formData.features} onChange={handleChange} /></label>
                <label>Technical Specs (one per line) <textarea name="specifications" value={formData.specifications} onChange={handleChange} /></label>
                <label className="full-width-field">Replace Asset Visual <input type="file" name="image" ref={fileInputRef} onChange={handleChange} accept="image/*" /></label>
                
                <div className="full-width-field" style={{ marginTop: '20px' }}>
                    {preview ? (
                        <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 800, marginBottom: '8px', color: '#64748b' }}>NEW PREVIEW</div>
                            <img src={preview} alt="Preview" width="150" style={{ borderRadius: '8px' }} />
                        </div>
                    ) : existingImage ? (
                        <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 800, marginBottom: '8px', color: '#64748b' }}>CURRENT ASSET</div>
                            <img src={`${API_BASE_URL}${existingImage}`} alt="Current" width="150" style={{ borderRadius: '8px' }} />
                        </div>
                    ) : null}
                </div>

                {error && <p style={{ color: '#ef4444', fontWeight: 700 }} className="full-width-field">{error}</p>}
                {success && <p style={{ color: '#22c55e', fontWeight: 700 }} className="full-width-field">{success}</p>}

                <div className="admin-submit-area" style={{ marginTop: '20px' }}>
                    <button type="submit" className="btn-primary-elite" style={{ background: '#6366f1' }} disabled={submitting}>
                        {submitting ? "Synchronizing..." : "CONFIRM UPDATE"}
                    </button>
                    <button type="button" className="btn-update" style={{ background: '#f1f5f9', border: 'none', marginLeft: '15px' }} onClick={() => navigate("/admin/panel")}>
                        CANCEL
                    </button>
                </div>
            </form>
        </div>
      </div>
    </div>
  );
}
